import { useTranslation } from 'react-i18next';
import { useMemo, useContext } from 'react';

import PowertrainTap from 'lib/Tracking/Srp/SearchFilters/EngineFilter/PowertrainTap';
import useEvents from 'lib/hooks/useEvents';
import { usePowertrainFilter } from 'lib/hooks/useSrpFilters';

import LabeledCheckBox from 'components/Shared/SearchFilter/CheckBox/LabeledCheckBox';
import SearchFilterSubHeader from 'components/Shared/SearchFilter/SearchFilterSubHeader';

import { SrpPowertrainName } from 'types/srpFilters';
import SrpEventContext from 'contexts/SrpEventContext';
import { FuelTypeFacet } from 'lib/AutolistAPI';
import classNames from 'classnames';
import { formatNumberWithCommas } from 'lib/utils';
import { colors } from 'components/styles/variables';

const powertrains: SrpPowertrainName[] = [
  'electric',
  'diesel',
  'gasoline',
  'hybrid',
  'hydrogenFuelCell',
  'naturalGas',
  'pluginHybrid',
];

interface PowertrainFilterProps {
  fuelTypeFacets?: FuelTypeFacet;
}

interface PowertrainOptionProps {
  type: SrpPowertrainName;
  label: string;
  isChecked: boolean;
  facetCount: string;
  hasZeroFacets: boolean;
  onTap: (label: string, isSelected: boolean) => void;
}

function PowertrainOption({
  type,
  label,
  isChecked,
  facetCount,
  hasZeroFacets,
  onTap,
}: PowertrainOptionProps): JSX.Element {
  return (
    <div className="powertrain-item" style={{ display: 'flex', alignItems: 'center' }}>
      <LabeledCheckBox
        label={label}
        aria-label={`${type}-label`}
        isChecked={isChecked}
        onClick={(isSelected): void => onTap(type, isSelected)}
        style={{ width: 'auto', paddingRight: '5px' }}
        disabled={hasZeroFacets}
      />
      <span className={classNames({ 'zero-facets': hasZeroFacets })}>{`(${facetCount})`}</span>
      <style jsx>{`
        .zero-facets {
          color: ${colors.G2};
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}

export default function PowertrainFilter(props: PowertrainFilterProps): JSX.Element {
  const logger = useEvents();
  const { sourcePage } = useContext(SrpEventContext);
  const { t } = useTranslation();
  const { value, setValue } = usePowertrainFilter();

  const facetCounts = useMemo(() => {
    function getFacetCount(type: SrpPowertrainName): { count: string; hasZeroFacets: boolean } {
      const hasZeroFacets =
        props.fuelTypeFacets?.[type] === undefined || props.fuelTypeFacets?.[type] === 0;
      return {
        count: hasZeroFacets ? '0' : formatNumberWithCommas(props.fuelTypeFacets?.[type] as number),
        hasZeroFacets,
      };
    }

    return {
      electric: getFacetCount('electric'),
      diesel: getFacetCount('diesel'),
      gasoline: getFacetCount('gasoline'),
      hybrid: getFacetCount('hybrid'),
      hydrogenFuelCell: getFacetCount('hydrogenFuelCell'),
      naturalGas: getFacetCount('naturalGas'),
      pluginHybrid: getFacetCount('pluginHybrid'),
    };
  }, [props.fuelTypeFacets]);

  function onTap(type: string, isSelected: boolean): void {
    setValue({ ...value, [type]: isSelected });
    logger.log(PowertrainTap(type, sourcePage));
  }

  return (
    <>
      <SearchFilterSubHeader subTitle={t('srp.filters.engine.powertrain')} />
      <div className="powertrain-container">
        {powertrains.map((type) => (
          <PowertrainOption
            key={type}
            type={type}
            label={t(`srp.filters.engine.${type}`)}
            isChecked={value[type] ?? false}
            facetCount={facetCounts[type].count}
            hasZeroFacets={facetCounts[type].hasZeroFacets}
            onTap={onTap}
          />
        ))}
      </div>
      <style jsx>{`
        .powertrain-container {
          margin-bottom: 8px;
        }

        .powertrain-item {
          display: flex;
          align-items: center;
        }

        .zero-facets {
          color: ${colors.G2};
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
}
